import { convertImageSize, Size } from './image-utils';
import { generateImage } from '../../ai/core/api/api';
import { IShapeDataModel, IPictureDataModel, IDrawingShapeDataModel, DataType } from '@tencent/slide-sdk-model';
import { Task } from './limit-concurrency';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

const getSizeFromUrl = (url: string): Size | null => {
    // eslint-disable-next-line prefer-destructuring
    const query = url.split('?')[1];
    if (!query) return null;

    const params = new URLSearchParams(query);
    const w = params.get('w');
    const h = params.get('h');
    if (!w || !h) return null;

    const width = +w;
    const height = +h;
    if (isNaN(width) || isNaN(height)) return null;

    return {
        width,
        height,
    };
};

/** 生成失败的兜底图片 */
const BACKUP_URL = 'https://docimg4.docs.qq.com/image/AgAABTW7Ya4hnqF4u2VHSZIAGp6G2s8F.png?w=1920&h=1080';

export class ImageGenerator {
    public get isRunning(): boolean {
        return this.task.activeTasks > 0;
    }

    public task: Task<typeof this.createImage>;
    public create: typeof this.createImage;

    public constructor() {
        this.task = new Task(this.createImage, 2);
        this.create = this.task.execute;
    }

    public cancelAll() {
        this.task.cancelAll();
    }

    private createImage = async (pictureDataModel: IShapeDataModel | IPictureDataModel, keyword: string) => {
        const shapeSize = this.getShapeSize(pictureDataModel);
        if (!shapeSize) return;

        const picSize = convertImageSize(shapeSize);
        const result = await generateImage({
            keyword,
            title: SlideDataServices.SlideTitleManager()?.title ?? '',
            width: picSize.width,
            height: picSize.height,
            upload: true,
        });
        let url = result?.generate_image.url ?? '';

        if (!url) {
            window.log.info('AIGenerator', 'get image failed, use fallback');
            url = BACKUP_URL;
        }

        // 从 url 中取实际的图片尺寸
        const realPicSize = getSizeFromUrl(url) ?? picSize;
        try {
            this.updateShapeImage(pictureDataModel, url, keyword, realPicSize);
        } catch (error) {
            console.error(error);
        }
    };

    private updateShapeImage(shape: IShapeDataModel | IPictureDataModel, url: string, keyword: string, size: Size) {
        const taregetRect = shape.props.spPr.xfrm?.ext;
        if (!taregetRect?.cx || !taregetRect?.cy) return;

        const targetRatio = taregetRect.cx / taregetRect.cy;
        const ratio = size.width / size.height;
        let w: number;
        let h: number;
        if (ratio > targetRatio) {
            w = size.height * targetRatio;
            h = size.height;
        } else {
            w = size.width;
            h = size.width / targetRatio;
        }

        const x = Math.floor(((size.width - w) / 2 / size.width) * 100000);
        const y = Math.floor(((size.height - h) / 2 / size.height) * 100000);

        if (shape.type === DataType.Shape) {
            shape.api.updateProps({
                nvPr: {
                    name: keyword,
                },
                spPr: {
                    blipFill: {
                        blip: { embed: url },
                        srcRect: {
                            l: x,
                            t: y,
                            r: x,
                            b: y,
                        },
                        egFillModeProperties: {
                            tile: {
                                flip: 1,
                                algn: 5,
                                sx: 100000,
                                sy: 100000,
                            },
                        },
                    },
                },
            });
        } else {
            shape.api.updateProps({
                nvPr: {
                    name: keyword,
                },
                blipFill: {
                    blip: { embed: url },
                    srcRect: {
                        l: x,
                        t: y,
                        r: x,
                        b: y,
                    },
                },
            });
        }
    }

    private getShapeSize(shape: IDrawingShapeDataModel): Size | undefined {
        const rect = shape.props.spPr.xfrm?.ext;
        if (!rect?.cx || !rect?.cy) return;

        return {
            width: rect.cx / 12700,
            height: rect.cy / 12700,
        };
    }
}
